import { Button, Flex, Typography, emailRegex } from '@iheartjane/reefer';
import { Form } from '@iheartjane/reefer-hook-form';
import type { ReactNode } from 'react';

export interface EmailEntryFormData {
  email: string;
}

export interface EmailEntryProps {
  /** Optional pre-filled email value (e.g., when returning here via Edit). */
  defaultEmail?: string;

  /** Errors returned by the existence-check (or any submission). */
  errors?: string[] | null;

  /** Function called when the Cancel button is clicked (typically closes the modal). */
  onCancel(): void;

  /** Function called when the user submits the email. The parent should run onCheckUserExists and transition screens. */
  onSubmit(data: EmailEntryFormData): void;

  /** Optional React node to display at the bottom of the form. */
  signInFooter?: ReactNode;

  /**
   * Optional React node(s) rendered as SSO buttons (Google, Apple, etc.) between the
   * submit button and the Cancel button. Apps choose which buttons to surface
   * (e.g. `<GoogleButton onClick={...} />`).
   */
  ssoButtons?: ReactNode;
}

export const EmailEntry = ({
  defaultEmail,
  errors,
  onCancel,
  onSubmit,
  signInFooter,
  ssoButtons,
}: EmailEntryProps) => {
  return (
    <Form
      name="email-entry"
      onSubmit={(data) => onSubmit(data as unknown as EmailEntryFormData)}
    >
      <Flex width="100%" flexDirection="column" alignItems="center">
        <Typography branded variant="h2-semibold" textAlign="center" mb={24}>
          Let&rsquo;s check if you have an account
        </Typography>
        <Form.TextField
          type="email"
          autocomplete="email"
          label="Email"
          name="email"
          defaultValue={defaultEmail}
          required
          requiredLabel
          requiredMessage="Please enter your email address."
          errorMessage="Please enter a valid email address (e.g., name@example.com)."
          errorOnBlur
          pattern={emailRegex}
          disableMobileInputStyling
          mb={16}
        />
        {errors && errors.length > 0 && (
          <Flex flexDirection="column" mt={-12} mb={12}>
            {errors.map((error) => (
              <Typography key={error} color="error" textAlign="center">
                {error}
              </Typography>
            ))}
          </Flex>
        )}
        <Form.SubmitButton
          data-testid="email-entry-submit-btn"
          full
          label="Sign up or sign in"
          mb={16}
          variant="primary"
        />
        {ssoButtons && (
          <Flex
            width="100%"
            flexDirection="column"
            alignItems="center"
            gap={16}
            mb={16}
          >
            {ssoButtons}
          </Flex>
        )}
        <Button
          full
          label="Cancel"
          mb={24}
          onClick={onCancel}
          variant="tertiary"
        />
        {signInFooter || (
          <Typography as="span" color="grays-mid" textAlign="center">
            Sign in to check your reservations, status updates, and personal
            details&mdash;securely backed by Jane Technologies.
          </Typography>
        )}
      </Flex>
    </Form>
  );
};
